/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { mercuryBaseQuery } from 'api/base'
import { Cart, PaymentMethod, Product, ProductGroup, ProductList } from 'models/shop'

/**********************************************************************************************************
 *   SHOP API
 **********************************************************************************************************/
export const shopAPI = createApi({
    reducerPath: 'shopAPI',
    baseQuery: mercuryBaseQuery,
    tagTypes: ['Cart', 'Product'],
    endpoints: (builder) => ({
        domainProductList: builder.query({
            query: () => 'client/ordering/domain',
            transformResponse: (response: { data: Product[] }) => response.data
        }),
        domains: builder.query({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const [availability, suggestion]: any = await Promise.all([
                    baseQuery({
                        url: 'client/ordering/domain/availability',
                        method: 'POST',
                        body: {
                            domain: _arg
                        }
                    }),
                    baseQuery({
                        url: 'client/ordering/domain/suggestion',
                        method: 'POST',
                        body: {
                            domain: _arg
                        }
                    })
                ])

                if (availability.error) {
                    return { error: availability.error }
                }

                if (suggestion.error) {
                    return { error: suggestion.error }
                }

                return {
                    data: {
                        availability: availability.data.data,
                        suggestion: suggestion.data.data
                    }
                }
            }
        }),
        domainAvailability: builder.query({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const domainAvailability: any = await baseQuery({
                    url: 'client/ordering/domain/availability',
                    method: 'POST',
                    body: {
                        domain: _arg
                    }
                })

                if (domainAvailability.error) {
                    return { error: domainAvailability.error }
                }

                return {
                    data: {
                        domain: domainAvailability.data.data,
                        name: _arg
                    }
                }
            }
        }),
        productGroups: builder.query({
            query: () => 'client/ordering/product-groups',
            transformResponse: (baseQueryReturnValue: { data: ProductGroup }) => baseQueryReturnValue.data
        }),
        productList: builder.query({
            query: (id) => `client/ordering/product-groups/${id}/products`,
            transformResponse: (baseQueryReturnValue: { data: ProductList }) => baseQueryReturnValue.data
        }),
        product: builder.query({
            query: (id) => `client/ordering/products/${id}`,
            transformResponse: (baseQueryReturnValue: { data: Product }) => baseQueryReturnValue.data,
            providesTags: ['Product']
        }),
        createCart: builder.mutation({
            query: (items) => ({
                url: 'client/ordering/cart',
                method: 'POST',
                body: items
            }),
            transformResponse: (baseQueryReturnValue: { data: Cart }) => baseQueryReturnValue.data
        }),
        viewCart: builder.query({
            query: (uuid) => `client/ordering/cart/${uuid}`,
            transformResponse: (baseQueryReturnValue: { data: Cart }) => baseQueryReturnValue.data,
            providesTags: ['Cart']
        }),
        updateCartItems: builder.mutation({
            async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
                const { uuid, ...rest } = _arg
                const updateCartItems: any = await baseQuery({
                    url: `client/ordering/cart/${uuid}`,
                    method: 'PUT',
                    body: rest
                })

                if (updateCartItems.error) {
                    return { error: updateCartItems.error }
                }

                return {
                    data: {
                        cart: updateCartItems.data.data,
                        ...rest
                    }
                }
            }
        }),
        deleteCart: builder.mutation({
            query: (uuid) => ({
                url: `client/ordering/cart/${uuid}`,
                method: 'DELETE'
            })
        }),
        paymentMethods: builder.query({
            query: () => 'client/ordering/payment-methods',
            transformResponse: (baseQueryReturnValue: { data: PaymentMethod[] }) => baseQueryReturnValue.data
        }),
        addPromoCodeToCart: builder.mutation({
            query: ({ uuid, ...rest }) => ({
                url: `client/ordering/cart/${uuid}/promo/add`,
                method: 'POST',
                body: rest
            }),
            invalidatesTags: ['Cart']
        }),
        removePromoCodeFromCart: builder.mutation({
            query: ({ uuid, code }) => ({
                url: `client/ordering/cart/${uuid}/promo/remove`,
                method: 'POST',
                body: { code }
            }),
            invalidatesTags: ['Cart']
        }),
        checkoutCart: builder.mutation({
            query: ({ uuid, ...rest }) => ({
                url: `client/ordering/cart/${uuid}/checkout`,
                method: 'POST',
                body: rest
            }),
            transformResponse: (baseQueryReturnValue: { data: null | { invoice: { id: null | number } } }) => baseQueryReturnValue.data
        })
    })
})

export const {
    useDomainProductListQuery,
    useDomainsQuery,
    useDomainAvailabilityQuery,
    useProductGroupsQuery,
    useProductListQuery,
    useProductQuery,
    useCreateCartMutation,
    useViewCartQuery,
    useUpdateCartItemsMutation,
    useDeleteCartMutation,
    usePaymentMethodsQuery,
    useAddPromoCodeToCartMutation,
    useRemovePromoCodeFromCartMutation,
    useCheckoutCartMutation
} = shopAPI
